import { useMemo, useState } from 'react';
import { NavBar } from '../components/NavBar';
import { MapView } from '../components/MapView';
import { RouteChip } from '../components/RouteChip';
import { BottomSheet } from '../components/BottomSheet';
import { FilterChips } from '../components/FilterChips';
import { RestaurantCard } from '../components/RestaurantCard';
import { useApp } from '../state/store';
import { useRestaurantsAlongRoute } from '../hooks/useRestaurantsAlongRoute';
import { applyFilters } from '../lib/restaurantFilter';
import { formatMinutes } from '../lib/format';
import type { Restaurant } from '@bilmat/shared';
import type { SortMode } from '../state/store';

function sortLabel(sort: SortMode): string {
  return sort === 'omvag' ? 'minst omväg' : 'högst betyg';
}

function sortRestaurants(items: Restaurant[], sort: SortMode): Restaurant[] {
  const copy = [...items];
  if (sort === 'omvag') copy.sort((a, b) => a.detourMinutes - b.detourMinutes);
  else copy.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
  return copy;
}

export function Screen3Results() {
  const route = useApp((s) => s.route);
  const origin = useApp((s) => s.origin);
  const destination = useApp((s) => s.destination);
  const timeWindow = useApp((s) => s.timeWindow);
  const eatTargetMin = useApp((s) => s.eatTargetMin);
  const sort = useApp((s) => s.sort);
  const setSort = useApp((s) => s.setSort);
  const filters = useApp((s) => s.filters);
  const selectedPlaceId = useApp((s) => s.selectedPlaceId);
  const selectRestaurant = useApp((s) => s.selectRestaurant);
  const setScreen = useApp((s) => s.setScreen);

  const { restaurants, isLoading, error, retry } = useRestaurantsAlongRoute(
    route,
    origin,
    destination,
    timeWindow,
  );

  const visible = useMemo(
    () => sortRestaurants(applyFilters(restaurants, filters), sort),
    [restaurants, filters, sort],
  );
  const [sortOpen, setSortOpen] = useState(false);

  const open = (placeId: string): void => {
    selectRestaurant(placeId);
    setScreen(4);
  };

  const timeLabel =
    eatTargetMin === 0 ? 'Äta så snart som möjligt' : `Äta om ${formatMinutes(eatTargetMin)}`;

  return (
    <div className="screen screen--results">
      <NavBar title="Restauranger" backTo={2} compact />
      <div className="results-map">
        {route && <MapView route={route} restaurants={visible} />}
        {route && (
          <div className="results-routechip">
            <RouteChip
              origin={route.origin}
              destination={route.destination}
              timeLabel={timeLabel}
            />
          </div>
        )}
      </div>

      <BottomSheet>
        <div className="sheet-header">
          <span className="sheet-title">{visible.length} längs vägen</span>
          <div className="sort-control">
            <button type="button" className="sort-btn" onClick={() => setSortOpen((v) => !v)}>
              Sortera: {sortLabel(sort)} ▾
            </button>
            {sortOpen && (
              <div className="sort-menu">
                {(['omvag', 'betyg'] as SortMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    className="sort-menu-item"
                    onClick={() => {
                      setSort(m);
                      setSortOpen(false);
                    }}
                  >
                    {sortLabel(m)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <FilterChips />

        <div className="sheet-list">
          {isLoading && <div className="sheet-empty">Söker restauranger…</div>}
          {!isLoading && error && (
            <div className="sheet-empty sheet-error">
              <p>Något gick fel när vi sökte. Kontrollera din anslutning.</p>
              <button type="button" className="retry-btn" onClick={retry}>
                Försök igen
              </button>
            </div>
          )}
          {!isLoading && !error && visible.length === 0 && (
            <div className="sheet-empty">
              Inga restauranger hittades. Prova att ändra tidsintervallet.
            </div>
          )}
          {!isLoading &&
            !error &&
            visible.map((r) => (
              <RestaurantCard
                key={r.placeId}
                restaurant={r}
                selected={r.placeId === selectedPlaceId}
                onOpen={open}
              />
            ))}
        </div>
      </BottomSheet>
    </div>
  );
}
